<template>
    <div class="space-y-6 | xs:flex xs:space-x-6 xs:space-y-0">
        <div class="flex-1">
            <label>{{ $t('responseLeadXpress.sender') }}</label>
            <activix-select
                class="w-full"
                :value="selectedSender"
                :options="senderOptions"
                @input="updateSender"
            >
                <template #default>
                    <activix-select-option
                        :label="option.label"
                        :value="option.value"
                        :key="option.value"
                        v-for="option in authUserOption"
                    />
                    <select-option-group :label="$t('responseLeadXpress.relativeFields')">
                        <activix-select-option
                            :label="relativeOwner.label"
                            :value="relativeOwner.value"
                            :key="relativeOwner.value"
                            v-for="relativeOwner in relativeOptions"
                        />
                    </select-option-group>
                    <select-option-group
                        :label="$t('responseLeadXpress.specificUser')"
                        v-if="displayUserOptions && activeUserOptions.length"
                    >
                        <activix-select-option
                            :label="option.label"
                            :value="option.value"
                            :key="option.value"
                            v-for="option in activeUserOptions"
                        />
                    </select-option-group>
                </template>
            </activix-select>
        </div>
        <div class="flex-1">
            <label>{{ $t('responseLeadXpress.senderBackup') }} </label>
            <activix-select
                class="w-full"
                :value="fromBackup"
                :disabled="!displayFromBackup"
                :options="backupOptions"
                @input="$emit('update:fromBackup', $event)"
            />
        </div>
    </div>
</template>

<script>
    import { ActivixSelect, ActivixSelectOption } from '@/components/activix-ui/index.ts';
    import SelectOptionGroup from '@/components/activix-ui/ActivixSelect/ActivixSelectOptionGroup.vue';
    import { useAccountStore } from '@/store/modules/account/store.js';
    import { concat } from 'lodash-es';
    import User from '@/entities/User.js';

    export default {
        components: {
            ActivixSelect,
            ActivixSelectOption,
            SelectOptionGroup,
        },

        emits: ['update:fromBackup', 'update:from'],

        props: {
            from: [String, Number],
            fromBackup: [String, Number],
            user: {
                type: User,
                required: true,
            },
            campaignSettings: {
                type: Object,
                default: null,
            },
        },

        data() {
            return {
                sendAs: [],
                authUserOption: [],
            };
        },

        computed: {
            contextAccount() {
                return useAccountStore().contextAccount;
            },

            activeUsers() {
                return this.contextAccount.activeUsers;
            },

            activeUserOptions() {
                return this.activeUsers
                    .filter((user) => {
                        if (user.id == this.user.id) {
                            return false;
                        }

                        if (this.campaignSettings) {
                            return user.campaign;
                        }

                        return true;
                    })
                    .map((user) => ({
                        value: user.id,
                        label: user.fullName,
                    }));
            },

            senderOptions() {
                const groupedOptions = concat(this.authUserOption, this.relativeOptions);

                if (this.displayUserOptions) {
                    return concat(groupedOptions, this.activeUserOptions);
                }

                return groupedOptions;
            },

            backupOptions() {
                if (!this.displayUserOptions) {
                    return this.authUserOption;
                }

                return concat(this.authUserOption, this.activeUserOptions);
            },

            relativeOptions() {
                return [
                    {
                        value: 'user_id',
                        label: this.$t('responseLeadXpress.leadRelatedUser.user'),
                    },
                    {
                        value: 'bdc_user_id',
                        label: this.$t('responseLeadXpress.leadRelatedUser.bdcUser'),
                    },
                    {
                        value: 'commercial_id',
                        label: this.$t('responseLeadXpress.leadRelatedUser.commercial'),
                    },
                    {
                        value: 'service_agent_id',
                        label: this.$t('responseLeadXpress.leadRelatedUser.serviceAgentUser'),
                    },
                    {
                        value: 'service_advisor_id',
                        label: this.$t('responseLeadXpress.leadRelatedUser.serviceAdvisorUser'),
                    },
                ];
            },

            displayFromBackup() {
                return !Number.isInteger(this.from);
            },

            displayUserOptions() {
                return (
                    this.user.isAdmin() ||
                    this.user.isDirector() ||
                    this.user.isBdcDirector() ||
                    this.user.isSuperBdc()
                );
            },

            selectedSender() {
                return this.senderOptions.find((option) => option.value === this.from)?.value;
            },
        },

        watch: {
            'contextAccount.id': {
                immediate: true,
                handler() {
                    this.setAuthUserOption();
                },
            },
        },

        methods: {
            setAuthUserOption() {
                const authUserOptionExists = this.activeUsers.find(
                    (user) => user.id === this.user.id
                );

                if (!authUserOptionExists || (this.campaignSettings && !this.user.campaign)) {
                    this.authUserOption = [];
                    this.updateSender(null);
                    return;
                }

                this.authUserOption = [
                    {
                        value: this.user.id,
                        label: `${this.user.fullName} (${this.$t('responseLeadXpress.myself')})`,
                    },
                ];
            },

            updateSender(event) {
                this.$emit('update:from', event);
            },
        },
    };
</script>
